/**
 * Default display locale for dates. `en-GB` renders DD/MM/YYYY.
 * Customers may override it via `customerConfig.dateTime.locale`.
 */
export const DEFAULT_DATE_LOCALE = 'en-GB';

const dateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;

const dateOnlyFormatOptions: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

export const isDateOnlyValue = (value: string): boolean => {
  return dateOnlyPattern.test(value);
};

export const parseDateValue = (value: string): Date | null => {
  if (isDateOnlyValue(value)) {
    const [year, month, day] = value.split('-').map(Number);
    const parsed = new Date(year, month - 1, day);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(value);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDateValue = (
  value: string,
  options: Intl.DateTimeFormatOptions,
  locales?: Intl.LocalesArgument,
): string | null => {
  const parsed = parseDateValue(value);

  if (!parsed) return null;

  return new Intl.DateTimeFormat(locales, options).format(parsed);
};

export const formatDateOnly = (
  value: string | Date,
  locales: Intl.LocalesArgument = 'en-GB',
): string | null => {
  const parsed = typeof value === 'string' ? parseDateValue(value) : value;

  if (!parsed || Number.isNaN(parsed.getTime())) return null;

  return new Intl.DateTimeFormat(locales, dateOnlyFormatOptions).format(parsed);
};

export const getDatePickerDateFormat = (locales?: Intl.LocalesArgument): string =>
  new Intl.DateTimeFormat(locales, dateOnlyFormatOptions)
    .formatToParts(new Date(2026, 0, 5))
    .map(({ type, value }) => {
      if (type === 'day') return 'dd';
      if (type === 'month') return 'mm';
      if (type === 'year') return 'yy';
      return value;
    })
    .join('');

const PATTERN_TOKEN_REGEX = /yyyy|yy|MM|dd|HH|mm|ss/g;

export const formatDateTimePattern = (date: Date, pattern: string): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  const year = date.getFullYear();

  return pattern.replace(PATTERN_TOKEN_REGEX, (token) => {
    switch (token) {
      case 'yyyy':
        return String(year);
      case 'yy':
        return pad(year % 100);
      case 'MM':
        return pad(date.getMonth() + 1);
      case 'dd':
        return pad(date.getDate());
      case 'HH':
        return pad(date.getHours());
      case 'mm':
        return pad(date.getMinutes());
      case 'ss':
        return pad(date.getSeconds());
      default:
        return token;
    }
  });
};

export type ResolvedDateFormat =
  { kind: 'pattern'; pattern: string } | { kind: 'locale'; locale: Intl.LocalesArgument };

export const resolveCustomerDateFormat = (
  config: { locale?: Intl.LocalesArgument; pattern?: string } | undefined,
): ResolvedDateFormat => {
  if (config?.pattern) return { kind: 'pattern', pattern: config.pattern };
  if (config?.locale) return { kind: 'locale', locale: config.locale };
  return { kind: 'locale', locale: undefined };
};

export const formatWithCustomer = (
  value: string | Date,
  intlOptions: Intl.DateTimeFormatOptions,
  config: { locale?: Intl.LocalesArgument; pattern?: string } | undefined,
): string => {
  const date = typeof value === 'string' ? new Date(value) : value;
  const target = resolveCustomerDateFormat(config);

  if (target.kind === 'pattern') {
    return formatDateTimePattern(date, target.pattern);
  }

  return new Intl.DateTimeFormat(target.locale, intlOptions).format(date);
};

export const toBrowserDateOnly = (date = new Date()): string => {
  return formatDateTimePattern(date, 'yyyy-MM-dd');
};

export const toUtcDateOnly = (date = new Date()): string => {
  return date.toISOString().slice(0, 10);
};

export const getIsoDateDaysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toBrowserDateOnly(date);
};
